import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';

const SubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await companyService.getSubscriptions();
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      const sub = (list || []).find(s => String(s.id) === String(id));
      if (!sub) {
        toast.error('الاشتراك غير موجود');
        navigate('/company/subscriptions');
        return;
      }
      setSubscription(sub);
    } catch (error) {
      const message = error?.response?.data?.message || 'فشل تحميل تفاصيل الاشتراك';
      toast.error(message);
      navigate('/company/subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const canCancel = (sub) => sub?.status === 'active';

  const handleCancel = async () => {
    if (!subscription) return;
    try {
      setCancelling(true);
      await companyService.cancelSubscription(subscription.id);
      toast.success('تم إلغاء الاشتراك بنجاح');
      await load();
    } catch (error) {
      const message = error?.response?.data?.message || 'فشل إلغاء الاشتراك';
      toast.error(message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  const plan = subscription?.plan || {};

  return (
    <div className="employee-home-page" dir="rtl">
      <CompanyNav user={user} />

      {/* Header */}
      <div className="px-3 mb-3 mt-3">
        <div className="edit-hero">
          <div className="edit-hero-inner">
            <button
              className="settings-back-btn"
              onClick={() => navigate(-1)}
              style={{ background: 'transparent', border: 0 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            <div className="edit-hero-badge">📄</div>
            <div className="edit-hero-text">
              <div className="edit-hero-title">تفاصيل الاشتراك</div>
              <div className="edit-hero-subtitle">#{subscription?.id} — {plan?.name || 'خطة'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="all-jobs-section px-3 mb-5">
        <div className="edit-card mb-4">
          <div className="grid-2">
            <div className="form-group-modern">
              <label className="form-label">الخطة</label>
              <div className="section-text">{plan?.name || '—'}</div>
            </div>
            <div className="form-group-modern">
              <label className="form-label">السعر</label>
              <div className="section-text">{plan?.price != null ? `${Number(plan.price).toFixed(2)} $` : '—'}</div>
            </div>
            <div className="form-group-modern">
              <label className="form-label">الحالة</label>
              <div>
                <span className={`status-badge ${subscription?.status === 'active' ? 'status-badge-accepted' : (subscription?.status === 'canceled' ? 'status-badge-rejected' : 'status-badge-warning')}`}>
                  {subscription?.status === 'active' ? 'نشط' : subscription?.status === 'canceled' ? 'ملغي' : 'منتهي'}
                </span>
              </div>
            </div>
            <div className="form-group-modern">
              <label className="form-label">المدة</label>
              <div className="section-text">{plan?.duration_days ? `${plan.duration_days} يوم` : '—'}</div>
            </div>
            <div className="form-group-modern">
              <label className="form-label">تاريخ البداية</label>
              <div className="section-text">{formatDate(subscription?.start_date)}</div>
            </div>
            <div className="form-group-modern">
              <label className="form-label">تاريخ النهاية</label>
              <div className="section-text">{formatDate(subscription?.end_date)}</div>
            </div>
          </div>

          {/* Plan limits */}
          <div className="grid-2" style={{ marginTop: 12 }}>
            {'vacancies_limit' in plan && (
              <div className="form-group-modern">
                <label className="form-label">عدد الوظائف</label>
                <div className="section-text">{plan.vacancies_limit ?? 'غير محدود'}</div>
              </div>
            )}
            {'users_limit' in plan && (
              <div className="form-group-modern">
                <label className="form-label">عدد المستخدمين</label>
                <div className="section-text">{plan.users_limit ?? 'غير محدود'}</div>
              </div>
            )}
            {'clients_limit' in plan && (
              <div className="form-group-modern">
                <label className="form-label">عدد العملاء</label>
                <div className="section-text">{plan.clients_limit ?? 'غير محدود'}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {plan?.description && (
            <div className="form-group-modern" style={{ marginTop: 12 }}>
              <label className="form-label">الوصف</label>
              <div
                className="text-muted"
                style={{ fontSize: '0.95rem' }}
                dangerouslySetInnerHTML={{ __html: plan.description }}
              />
            </div>
          )}

          {/* Actions */}
          <div className="actions" style={{ marginTop: 16 }}>
            {canCancel(subscription) && (
              <button
                onClick={handleCancel}
                className="btn-withdraw"
                disabled={cancelling}
              >
                {cancelling ? 'جارٍ الإلغاء...' : 'إلغاء الاشتراك'}
              </button>
            )}
          </div>
        </div>

        {/* Local styles */}
        <style>
          {`
            .edit-hero {
              background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
              border-radius: 18px;
              padding: 14px 16px;
            }
            .edit-hero-inner {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .edit-hero-badge {
              width: 44px;
              height: 44px;
              border-radius: 12px;
              display: grid;
              place-items: center;
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              font-size: 22px;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .edit-hero-title {
              font-weight: 800;
              font-size: 1.1rem;
            }
            .edit-hero-subtitle {
              color: #475569;
              font-size: 0.9rem;
            }
            .edit-card {
              background: #ffffff;
              border-radius: 16px;
              padding: 18px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.05);
              border: 1px solid #eef2f7;
            }
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr;
              gap: 14px;
            }
            @media (min-width: 768px) {
              .grid-2 {
                grid-template-columns: 1fr 1fr;
              }
            }
            .form-group-modern {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
          `}
        </style>
      </div>

      <CompanyBottomNavigation />
    </div>
  );
};

export default SubscriptionDetail;



